import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';
import { Fragment, Slice } from '@tiptap/pm/model';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Code,
  Link2,
  Image as ImageIcon,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useSnippetMenu } from './SnippetMenu';
import { ImagePicker } from './ImagePicker';
import { emailImagesAPI } from '../../api/endpoints';
import { blobForImage, registerEmailImage } from '../../utils/emailImages';
import { downscaleImageFile, imageFileFrom } from '../../utils/imageUpload';
import type { EmailImage } from '../../types';

const FONT_SIZES = [
  { label: 'Small', value: '12px' },
  { label: 'Normal', value: '' },
  { label: 'Large', value: '18px' },
  { label: 'X-Large', value: '24px' },
];

const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (size: string) =>
        ({ chain }: { chain: () => ReturnType<ReturnType<typeof useEditor>['chain']> }) => {
          if (!size) {
            return chain().setMark('textStyle', { fontSize: null }).run();
          }
          return chain().setMark('textStyle', { fontSize: size }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }: { chain: () => ReturnType<ReturnType<typeof useEditor>['chain']> }) => {
          return chain().setMark('textStyle', { fontSize: null }).run();
        },
    } as Record<string, (...args: unknown[]) => unknown>;
  },
});

function ToolbarButton({
  onClick,
  isActive,
  children,
  title,
}: {
  onClick: () => void;
  isActive?: boolean;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={clsx(
        'p-2 rounded-sm hover:bg-dark-elevated transition-colors',
        isActive ? 'bg-primary-600 text-white' : 'text-dark-text-secondary'
      )}
    >
      {children}
    </button>
  );
}

export interface RichTextEditorHandle {
  /**
   * Replace the document. For a caller that does not mirror the html in state — the contact
   * overlay's composer, which keeps it in a ref so typing never re-renders — this is how a
   * template or a reset gets in.
   */
  setContent: (html: string) => void;
}

interface RichTextEditorProps {
  /**
   * The document's content. A caller may mirror every `onChange` back into it (fully
   * controlled), or pass a seed once and push later changes through the ref.
   */
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /**
   * Whether typing `/` offers the user's saved snippets (docs/EMAIL_SNIPPETS.md). On by default;
   * off where a snippet is itself being written, in Profile -> Settings.
   */
  enableSnippets?: boolean;
  /**
   * Whether the toolbar offers the image library, and a pasted or dropped image is uploaded into
   * it (docs/EMAIL_IMAGES.md). On by default — it exists for the next editor that should not have
   * images, not because any current one wants it off.
   */
  enableImages?: boolean;
  ref?: Ref<RichTextEditorHandle>;
}

export function RichTextEditor({
  content,
  onChange,
  enableSnippets = true,
  enableImages = true,
  ref,
}: RichTextEditorProps) {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  /**
   * How `editorProps` reaches the upload handler. `editorProps` is captured once, when the editor
   * is created, so a handler written inline there would close over the first render's bindings —
   * and on that render the component returns early (the editor is still null), leaving them in
   * the temporal dead zone and throwing on the first paste. Same reason `Table` routes its row
   * handlers through a ref (docs/TABLE_PERF.md). Null when images are off.
   */
  const uploadImageRef = useRef<((file: File) => void) | null>(null);

  const lastExternalContent = useRef(content);
  const snippetMenu = useSnippetMenu(enableSnippets);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      TextStyle,
      FontSize,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary-400 underline hover:text-primary-300',
        },
      }),
      // allowBase64 off is the structural guarantee behind docs/EMAIL_IMAGES.md: with it off
      // there is no path by which image bytes can enter the document, and so none by which they
      // can reach Mongo. Everything an image needs is a library reference.
      Image.configure({ allowBase64: false, inline: false }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
      snippetMenu.sync(editor);
    },
    onSelectionUpdate: ({ editor }) => snippetMenu.sync(editor),
    onFocus: ({ editor }) => snippetMenu.sync(editor),
    onBlur: () => snippetMenu.close(),
    editorProps: {
      handleKeyDown: snippetMenu.handleKeyDown,
      attributes: {
        class: 'max-w-none',
      },
      // A pasted or dropped image *file* — a screenshot, a drag from the desktop — goes into the
      // library and is inserted as a reference. Returning true claims the event so ProseMirror
      // does not also insert whatever HTML the clipboard carried alongside it.
      handlePaste(_view, event) {
        const file = imageFileFrom(event.clipboardData?.items ?? null);
        if (file && uploadImageRef.current) {
          uploadImageRef.current(file);
          return true;
        }
        return false;
      },
      handleDrop(_view, event) {
        const transfer = (event as DragEvent).dataTransfer;
        const file = imageFileFrom(transfer?.files ?? null);
        if (file && uploadImageRef.current) {
          event.preventDefault();
          uploadImageRef.current(file);
          return true;
        }
        return false;
      },
      transformPastedHTML(html) {
        // Gmail separates paragraphs with <div><br></div> (an empty div containing
        // only a <br>). TipTap drops empty block elements when parsing, losing the
        // blank line. Replace each such empty div with a sentinel paragraph so the
        // blank line is preserved as an empty <p> in the editor.
        const withBlankLines = html.replace(/<div[^>]*>\s*<br\s*\/?>\s*<\/div>/gi, '<p><br></p>');
        // Drop images pasted in from a web page or another mail. Keeping one would mean a mail
        // whose image is hotlinked from someone else's server: broken for the recipient the day
        // it moves, blocked by default in most clients, and a tracking beacon when the source was
        // a received mail. A pasted *file* (a screenshot) is uploaded instead — see handlePaste.
        return withBlankLines.replace(/<img\b[^>]*>/gi, (tag) =>
          /src=["']?(?:blob:|cid:bmb-)/i.test(tag) ? tag : ''
        );
      },
      clipboardTextParser(text, context) {
        const schema = context.node(0).type.schema;
        const paragraphType = schema.nodes.paragraph;
        const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const blocks = normalized.split('\n');
        const nodes = blocks.map((line) => {
          if (line.trim() === '') {
            return paragraphType.create();
          }
          return paragraphType.create(null, schema.text(line));
        });
        return new Slice(Fragment.from(nodes), 0, 0);
      },
    },
  });

  // Only reset editor content for external changes (e.g. template selection, clear). A value
  // that merely echoes our own `onChange` is already in the document, and re-setting it would
  // drop the caret to the end mid-sentence — so compare against what the editor actually holds
  // rather than tracking which side last spoke.
  useEffect(() => {
    if (!editor) return;
    if (content === lastExternalContent.current) return;
    lastExternalContent.current = content;
    if (content === editor.getHTML()) return;
    editor.commands.setContent(content);
  }, [content, editor]);

  // `lastExternalContent` deliberately stays untouched here: it tracks what the `content` prop
  // last said, and a push through the ref is not the prop changing. Writing it would make the
  // effect above fire on the next render that the prop happens to differ from.
  useImperativeHandle(ref, () => ({
    // setContent emits an update by default, so the caller's `onChange` still fires
    setContent: (html: string) => editor?.commands.setContent(html),
  }), [editor]);

  if (!editor) {
    return null;
  }

  // An image is inserted by the blob URL the library holds for it: a `cid:` will not display, and
  // the blob URL is also the handle `toStoredHtml` maps back to a reference on the way out, which
  // is why the TipTap image node needs no custom attribute. See docs/EMAIL_IMAGES.md.
  const insertImage = (image: EmailImage) => {
    const src = blobForImage(image.id);
    if (!src) {
      toast.error('That image is still loading — try again in a moment');
      return;
    }
    editor
      .chain()
      .focus()
      .setImage({
        src,
        alt: image.filename,
        // Outlook renders an image at its native pixel size without an explicit width
        ...(image.width ? { width: Math.min(image.width, 600) } : {}),
      })
      .run();
    setShowImagePicker(false);
  };

  const uploadAndInsert = async (file: File) => {
    setIsUploadingImage(true);
    try {
      const { file: downscaled, width, height } = await downscaleImageFile(file);
      const { data } = await emailImagesAPI.upload(downscaled, width, height);
      registerEmailImage(data);
      insertImage(data);
    } catch (error) {
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (error as Error)?.message ??
        'Could not upload that image';
      toast.error(detail);
    } finally {
      setIsUploadingImage(false);
    }
  };

  uploadImageRef.current = enableImages ? (file: File) => void uploadAndInsert(file) : null;

  const handleAddLink = () => {
    if (!linkUrl) return;
    // Only allow safe protocols — reject javascript:, data:, vbscript:, etc.
    try {
      const parsed = new URL(linkUrl);
      if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) return;
    } catch {
      return; // not a valid absolute URL
    }
    if (linkText) {
      // Insert link with text using tiptap's setLink to avoid raw HTML injection
      editor
        .chain()
        .focus()
        .insertContent(linkText)
        .setTextSelection({ from: editor.state.selection.from - linkText.length, to: editor.state.selection.from })
        .setLink({ href: linkUrl })
        .run();
    } else {
      // Set link on selected text
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setLinkUrl('');
    setLinkText('');
    setShowLinkInput(false);
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkInput(false);
  };

  return (
    <div className="border border-dark-border rounded-lg bg-dark-elevated overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-dark-border p-2 flex items-center gap-1 flex-wrap">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Inline Code"
        >
          <Code className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-dark-border mx-1" />

        <select
          title="Font Size"
          value={editor.getAttributes('textStyle').fontSize || ''}
          onChange={(e) => {
            const size = FONT_SIZES.find(s => s.value === e.target.value)?.value ?? '';
            if (size) {
              editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
            } else {
              editor.chain().focus().setMark('textStyle', { fontSize: null }).run();
            }
          }}
          className="px-2 py-1 text-sm bg-dark-elevated border border-dark-border rounded-sm text-dark-text-secondary hover:bg-dark-surface transition-colors cursor-pointer"
        >
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <div className="w-px h-6 bg-dark-border mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Blockquote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-dark-border mx-1" />

        <ToolbarButton
          onClick={() => setShowLinkInput(!showLinkInput)}
          isActive={editor.isActive('link')}
          title="Insert Link"
        >
          <Link2 className="w-4 h-4" />
        </ToolbarButton>

        {enableImages && (
          <div className="relative">
            <ToolbarButton
              onClick={() => setShowImagePicker((open) => !open)}
              isActive={showImagePicker || editor.isActive('image')}
              title={isUploadingImage ? 'Uploading image…' : 'Insert Image'}
            >
              <ImageIcon className={clsx('w-4 h-4', isUploadingImage && 'animate-pulse')} />
            </ToolbarButton>
            {showImagePicker && (
              <ImagePicker onPick={insertImage} onClose={() => setShowImagePicker(false)} />
            )}
          </div>
        )}

        <div className="w-px h-6 bg-dark-border mx-1" />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Link Input */}
      {showLinkInput && (
        <div className="border-b border-dark-border p-2 flex items-center gap-2 bg-dark-surface">
          <input
            type="text"
            placeholder="Link text (optional)"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            className="flex-1 px-2 py-1 text-sm bg-dark-elevated border border-dark-border rounded-sm text-dark-text-primary"
          />
          <input
            type="url"
            placeholder="URL (https://...)"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            className="flex-1 px-2 py-1 text-sm bg-dark-elevated border border-dark-border rounded-sm text-dark-text-primary"
          />
          <button
            type="button"
            onClick={handleAddLink}
            className="px-3 py-1 text-sm bg-primary-600 text-white rounded-sm hover:bg-primary-700"
          >
            Add
          </button>
          {editor.isActive('link') && (
            <button
              type="button"
              onClick={handleRemoveLink}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700"
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
              setLinkText('');
            }}
            className="px-2 py-1 text-sm text-dark-text-secondary hover:text-dark-text-primary"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* "/" snippet menu — portals to the body, so it escapes this box's overflow-hidden */}
      {snippetMenu.menu}
    </div>
  );
}
